// src/components/LayerPanel/RrcInfoPanel.jsx
//
// FEATURES tab → RRC sub-tab. The shared/global RRC well layers, which are
// static/configured rather than org-derived (see layerPanelConfigStatic.js) and
// keep their point+line geometry sub-rows.
//
// Renders FLAT — one row per layer, no group headers and nothing to expand,
// matching the derived feature list in the sibling sub-tab. The configured
// groups are still a tree in layerPanelConfigStatic.js; this panel walks that
// tree and hoists every layer out of it.
//
// The collapsible OpGroup this file arrived with was wrapping a grand total of
// two rows, so the group chrome cost a click and bought nothing. Consequence to
// know about: group-level config keys — `defaultOpen`, `comingSoon`, and
// `filters` — are no longer honored, because there is no group UI left to hang
// them on. Nothing in LAYER_GROUPS_STATIC sets any of them today. The per-LAYER
// `comingSoon` flag still works; that one lives on the row.
//
// The per-tab search filters the flat list.

import { useMemo, useState } from "react";
import {
  Search,
  X,
  SlidersHorizontal,
  LocateFixed,
  Sparkles,
} from "lucide-react";
import * as LucideIcons from "lucide-react";

import { useAppNav } from "../../hooks/useAppNav";
import { useLayersStore } from "../../stores/useLayersStore";
import { useMapActions } from "../../stores/useMapActions";
import { useLayerCoverageStore } from "../../stores/useLayerCoverageStore";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import ToggleSwitch from "./ToggleSwitch";

// Format counts with locale grouping ("96,972") so they read cleanly.
const fmtCount = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString() : null;

function cx(...c) { return c.filter(Boolean).join(" "); }

// Layer ids that are "relevant to" each product, used for the contextual
// Sparkles hint. Ported from the old LayerPanel.
const PRODUCT_CONTEXT = {
  siteid: ["siteid-polygons", "siteid-polygons-outline", "siteid-lines", "siteid-points"],
  well_finder: ["wells"],
  padrekko: ["pads.well_pad"],
  explore: [],
};

// Dynamic lucide icon lookup — scoped to this panel; only feature rows use it.
function Icon({ name, size = 13, className }) {
  const Comp = LucideIcons[name];
  if (!Comp) return null;
  return <Comp size={size} className={className} />;
}

/* ─────────────────────────────────────────────
   OPERATIONAL LAYER ROW
───────────────────────────────────────────── */
function OpLayerRow({ layer, asset, contextActive }) {
  const visibleById = useLayersStore((s) => s.visibleById);
  const assets = useLayersStore((s) => s.assets);
  const opacityById = useLayersStore((s) => s.opacityById);
  const toggleLayer = useLayersStore((s) => s.toggleLayer);
  const toggleAssetGeometry = useLayersStore((s) => s.toggleAssetGeometry);
  const setOpacity = useLayersStore((s) => s.setOpacity);
  const boundsById = useLayersStore((s) => s.boundsById);
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);
  const coverage = useLayerCoverageStore((s) => s.coverage);
  const [showSlider, setShowSlider] = useState(false);

  // Coverage count for the active operating AOI, when known. Hidden in global
  // mode (coverage === null) or for entries without assetType metadata.
  const countLabel =
    coverage && layer.assetType
      ? fmtCount(coverage[layer.assetType]?.count)
      : null;

  const geomIcon = { point: "•", line: "―", polygon: "▭" }[layer.geometry];

  // "Coming soon" rows are inert. Rendered after the hooks above so hook
  // order stays stable for every row instance.
  if (layer.comingSoon) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-[5px] rounded border border-slate-800/50 opacity-40 cursor-not-allowed">
        {layer.icon && <Icon name={layer.icon} size={12} className="text-fg-secondary" />}
        {!layer.icon && geomIcon && (
          <span className="text-gray-700 text-[10px] font-bold w-3 text-center flex-shrink-0">{geomIcon}</span>
        )}
        <span className="flex-1 text-[11px] text-fg-secondary truncate">{layer.label}</span>
        <span className="text-[8px] font-mono text-gray-700 flex-shrink-0 px-1 py-px rounded bg-slate-800">soon</span>
      </div>
    );
  }

  const on = asset
    ? assets?.[asset]?.enabled && assets?.[asset]?.geometry?.[layer.geometry]
    : visibleById[layer.id];
  const opacity = opacityById[layer.id] ?? 1;
  const bounds = boundsById[layer.id];

  const onToggle = () => {
    if (asset && layer.geometry) toggleAssetGeometry(asset, layer.geometry);
    else toggleLayer(layer.id);
  };

  return (
    <div
      className={cx(
        "group rounded border transition",
        on ? "bg-bg-softer border-slate-700" : "bg-bg border-slate-800 hover:border-slate-700",
        contextActive && !on && "border-accent-info/30 bg-accent-info/5"
      )}
    >
      <div className="flex items-center gap-1.5 px-2 py-[5px]">
        {on ? (
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
        ) : layer.icon ? (
          <Icon name={layer.icon} size={12} className="text-fg-secondary" />
        ) : geomIcon ? (
          <span className="text-fg-secondary text-[10px] font-bold w-3 text-center flex-shrink-0">{geomIcon}</span>
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-transparent flex-shrink-0" />
        )}

        <button onClick={onToggle} className="flex-1 text-left min-w-0">
          <span className={cx("text-[11px] truncate block", on ? "text-gray-200" : "text-gray-400 hover:text-gray-200")}>
            {layer.label}
          </span>
        </button>

        {countLabel && (
          <span
            className="flex-shrink-0 text-[10px] font-mono text-fg-secondary"
            title="Features in current operating AOI"
          >
            {countLabel}
          </span>
        )}

        {contextActive && !on && (
          <span className="flex-shrink-0" title="Relevant to active product">
            <Sparkles size={10} className="text-accent-info/70" />
          </span>
        )}

        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
          <button onClick={() => setShowSlider((v) => !v)} className="p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-gray-300">
            <SlidersHorizontal size={10} />
          </button>
          {bounds && (
            <button onClick={() => zoomToBounds(bounds)} className="p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-accent-action">
              <LocateFixed size={10} />
            </button>
          )}
        </div>

        <button onClick={onToggle} className="flex-shrink-0">
          <ToggleSwitch on={on} />
        </button>
      </div>

      {on && showSlider && (
        <div className="px-3 pb-2">
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => setOpacity(layer.id, Number(e.target.value))}
            className="w-full accent-accent-info"
          />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   FLATTEN
───────────────────────────────────────────── */
// Walk a configured group and hoist every layer, at any depth, into one list.
// `asset` — the store key a row toggles through (see OpLayerRow's onToggle) —
// is inherited from the nearest ancestor that declares one, which is how it
// resolved when the tree rendered nested.
function flattenLayers(container, inheritedAsset) {
  const asset = container.asset ?? inheritedAsset;
  return [
    ...(container.layers ?? []).map((layer) => ({ layer, asset })),
    ...(container.groups ?? []).flatMap((g) => flattenLayers(g, asset)),
  ];
}

/* ─────────────────────────────────────────────
   RRC PANEL
───────────────────────────────────────────── */
export default function RrcInfoPanel({ userRole = "viewer" }) {
  const groups = useLayersStore((s) => s.groups);
  const { activeTab } = useAppNav();
  const [search, setSearch] = useState("");

  const coverage = useLayerCoverageStore((s) => s.coverage);
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);

  const activeProduct = activeTab ?? "explore";
  const contextLayerIds = PRODUCT_CONTEXT[activeProduct] ?? [];

  // Role filter, plus an explicit exclusion of the imagery-derived groups.
  //
  // useLayersStore.groups is buildLayerGroups() = deriveImageryGroups() +
  // LAYER_GROUPS_STATIC, and the imagery half is dead today only by accident:
  // MapPage.jsx:294 passes `{ imageryLayers }` while the store destructures
  // `{ layers = [] }` (useLayersStore.js:151), so nothing is ever derived.
  // Imagery has its own tab (fed by useImageryStackStore, not this), so if that
  // param name is ever reconciled, basin groups must not silently appear here.
  //
  // The filter is negative — exclude `imagery.` — rather than positive on the
  // `operational.` prefix, so a future static group lands here by default,
  // which is the behaviour this tree has always had.
  const roleGroups = useMemo(
    () =>
      (groups ?? []).filter(
        (g) =>
          !g.id?.startsWith("imagery.") &&
          (!g.roles || g.roles.includes(userRole))
      ),
    [groups, userRole]
  );

  // Every configured layer, hoisted out of the group tree, then narrowed by
  // coverage and by the search box. Config order is preserved rather than
  // sorted alphabetically: the RRC pair is curated (surface, then laterals),
  // and that ordering carries meaning the label sort would throw away.
  const rows = useMemo(() => {
    let out = roleGroups.flatMap((g) => flattenLayers(g, undefined));

    // Coverage filter: when coverage is loaded (AOI mode + fetch succeeded),
    // hide entries whose assetType has present:false. Entries without
    // assetType metadata (e.g. comingSoon stubs) stay visible.
    if (coverage) {
      out = out.filter(
        ({ layer }) =>
          !layer.assetType || coverage[layer.assetType]?.present !== false
      );
    }

    const q = search.trim().toLowerCase();
    if (q) out = out.filter(({ layer }) => layer.label?.toLowerCase().includes(q));

    return out;
  }, [roleGroups, coverage, search]);

  // "Empty" = AOI active, coverage loaded, every wired RRC assetType is
  // present:false. The hint sits above the list even when unwired rows still
  // draw below — the user knows the wired set is empty for this AOI.
  //
  // The v1 TODO about folding "well_pad" in here is moot for this panel now:
  // pads are a derived vector_asset and live in the FEATURES sub-tab, which
  // answers for its own emptiness. Each sub-tab speaking only for its own scope
  // is the composition question that TODO was deferring.
  // All four RRC types the tile path serves. At two, this hint fired whenever
  // surface wells and laterals were both absent from an AOI, even where that
  // AOI held injection wells or permitted locations — telling the user the RRC
  // set was empty while two of its rows had counts beside them.
  const WIRED_ASSET_TYPES = [
    "rrc_well_surface", "rrc_well_lateral", "rrc_well_uic", "rrc_permit",
  ];
  const isEmptyForAoi =
    activeOperatingAoiId &&
    coverage &&
    WIRED_ASSET_TYPES.every((t) => coverage[t]?.present === false);

  if (!groups?.length) {
    return <div className="p-4 text-[11px] text-fg-secondary">Loading layers…</div>;
  }

  return (
    <div className="px-2 py-2 space-y-2">
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search RRC layers…"
          aria-label="Search RRC layers"
          className={cx(
            "w-full pl-6 pr-6 py-1.5 rounded-md border bg-bg text-[11px] text-gray-200",
            "placeholder-gray-600 focus:outline-none transition",
            search
              ? "border-accent-info/60 focus:border-accent-info"
              : "border-slate-700 focus:border-accent-info/60"
          )}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-gray-200"
          >
            <X size={11} />
          </button>
        )}
      </div>

      {isEmptyForAoi && (
        <div className="px-2 py-1 text-[11px] text-fg-secondary italic">
          No features in this operating AOI.
        </div>
      )}

      {/* Same card shell the derived feature list uses, so the two sub-tabs
          read as one component with two sources rather than two designs. */}
      <div className="rounded-md border border-slate-800 bg-bg-soft overflow-hidden">
        <div className="px-2 py-2 space-y-1">
          {rows.length === 0 && (
            <div className="px-1 py-1 text-[11px] text-fg-secondary italic">
              {search ? "No matches." : "No RRC layers available."}
            </div>
          )}
          {rows.map(({ layer, asset }) => (
            <OpLayerRow
              key={layer.id}
              layer={layer}
              asset={asset}
              contextActive={contextLayerIds?.includes(layer.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
